import { parseCSV, lerp, fetchFromURL } from '../helpers';
import { animateRobot, getTableIndex, updateCamera } from '../robotFunctions/animateRobot';
import { Element, FileUploader, Select, Checkbox, Button, Slider, Container, TextInput, TextBox } from '../ui/pageElements';
import { TimeBar } from '../ui/timeBar';
import { recurseMaterialTraverse, cloneMaterial } from '../robotFunctions/robotHelpers';
import { mapLinear } from 'three/src/math/MathUtils';
import * as T from 'three';

/**
 * This handles everything involving motion with the robot
 * Everything in the bottom bar is connected to this.
 */
export class AnimationInterface {
    constructor(robotGroup, scene, interfaces) {
        this.robotGroup = robotGroup
        this.robot = robotGroup.robot;

        this.ghostRobots = []

        this.ghostRobot = this.robot.clone();
        this.ghostRobot.traverse(c => {
            if (c.material) {
                cloneMaterial(c);
                recurseMaterialTraverse(c.material, (material) => {
                    material.opacity = 0.5;
                })
            }
        })
        interfaces.scene.add(this.ghostRobot);
        this.ghostRobot.visible = false;

        this.scene = scene;
        this.allAnimations = [];
        this.currAnimationTable = [];
        this.inBetweenFrames = 1;
        this.interfaces = interfaces;
        this.animationURLs = [];
        this.lastTime = new Date();
        this.exactTime = 0;
        this.parallelMotions = [];
        let domElement = "bottomDiv"

        // time
        this.animationSliderTextInput = new TextInput("Time", domElement);
        this.animationSliderTextInput.setToolTip(this.interfaces.tooltips.time, "bottomToolTip");
        this.animationSliderTextInput.textInput.value = 0;
        this.animationSliderTextInput.textInput.onchange = () => {
            this.exactTime = Number(this.animationSliderTextInput.textInput.value);
            this.updateTime(this.exactTime);
        }

        // play and camera checkboxes
        this.playCheck = new Checkbox("play", domElement, false);
        this.playCheck.setToolTip(this.interfaces.tooltips.play, "bottomToolTip");
        this.autoCameraCheck = new Checkbox("camera", domElement, false);
        this.autoCameraCheck.setToolTip(this.interfaces.tooltips.camera, "bottomToolTip");
        this.playContainer = new Container("playDiv", domElement, true)
        this.playContainer.addBlock(this.playCheck, this.autoCameraCheck);

        // bookmark buttons
        this.bookmarkButton = new Button("Bookmark", domElement, () => {
            this.timeBar.addBookmark(this.animationPicker.getSelectedName(), this.exactTime);
        })
        this.bookmarkButton.setToolTip(this.interfaces.tooltips.mark, "bottomToolTip");
        this.cameraMarkButton = new Button("Camera Mark", domElement, () => {
            this.timeBar.addCameraMark(this.animationPicker.getSelectedName(), this.exactTime);
        })
        this.cameraMarkButton.setToolTip(this.interfaces.tooltips.cameraMark, "bottomToolTip");
        this.markContainer = new Container("markContainer", domElement, true);
        this.markContainer.addBlock(this.bookmarkButton, this.cameraMarkButton);

        // speed input
        this.speedTextInput = new TextInput("Speed", domElement);
        this.speedTextInput.setToolTip(this.interfaces.tooltips.speed, "bottomToolTip");
        this.speedTextInput.textInput.value = 1;

        // loop and bounce checkboxes
        this.loopCheck = new Checkbox("loop", domElement, true);
        this.loopCheck.setToolTip(this.interfaces.tooltips.loop, "bottomToolTip");
        this.bounceCheck = new Checkbox("bounce", domElement, false);
        this.bounceCheck.setToolTip(this.interfaces.tooltips.bounce, "bottomToolTip");
        this.loopCheck.checkbox.onchange = () => {
            this.bounceCheck.checkbox.checked = this.bounceCheck.checkbox.checked && !this.loopCheck.checkbox.checked;
        }
        this.bounceCheck.checkbox.onchange = () => {
            this.loopCheck.checkbox.checked = this.loopCheck.checkbox.checked && !this.bounceCheck.checkbox.checked;
        }
        this.playModeContainer = new Container("playCheckDiv", domElement, true);
        this.playModeContainer.addBlock(this.loopCheck, this.bounceCheck);

        // time step input and row step checkbox
        this.stepAmountTextInput = new TextInput("Time Step", domElement);
        this.stepAmountTextInput.setToolTip(this.interfaces.tooltips.timeStep, "bottomToolTip");
        this.stepAmountTextInput.textInput.value = 0.001;
        this.rowStepCheck = new Checkbox("Row Step", domElement, true);
        this.rowStepCheck.setToolTip(this.interfaces.tooltips.rowStep, "bottomToolTip");

        // step buttons and display
        this.stepContainer = new Container("stepContainer", domElement, true);
        this.stepButtonContainer = new Container("stepButtonContainer", domElement);
        this.backStepButton = new Button("Step Back", domElement, () => {
            this.step(-Number(this.stepAmountTextInput.textInput.value))
        })
        this.backStepButton.setToolTip(this.interfaces.tooltips.stepBack, "bottomToolTip");
        this.backStepButton.button.innerHTML = "<";
        this.forwardStepButton = new Button("Step Forward", domElement, () => {
            this.step(Number(this.stepAmountTextInput.textInput.value))
        })
        this.forwardStepButton.setToolTip(this.interfaces.tooltips.stepForward, "bottomToolTip");
        this.forwardStepButton.button.innerHTML = ">";
        this.stepText = new TextBox("stepText", domElement, "Step 0/0");
        this.stepText.setToolTip(this.interfaces.tooltips.stepDisplay, "bottomToolTip");
        this.stepButtonContainer.addBlock(this.backStepButton, this.forwardStepButton);
        this.stepContainer.addBlock(this.stepButtonContainer, this.stepText);

        // upload motion file
        this.loader = new FileUploader("Upload Motion", domElement, [".csv", ".rmoo"]);
        this.loader.setToolTip(this.interfaces.tooltips.uploadMotion, "bottomToolTip");
        this.loader.fileUploader.onchange = () => {
            if (this.loader.getFiles().length > 0) {
                parseCSV(this.loader.getFiles()[0], (data) => {
                    this.createAnimationTable(data);
                });
            }
        }

        // motion file link and button
        this.urlInput = new TextInput("Motion File URL", domElement);
        this.urlInput.setToolTip(this.interfaces.tooltips.motionFileLink, "bottomToolTip");
        this.loadURL = new Button("Load", domElement, () => {
            this.loadAnimation(this.urlInput.textInput.value);
        })
        this.loadURL.setToolTip(this.interfaces.tooltips.motionFileLinkButton, "bottomToolTip");

        // motion file picker
        this.animationPicker = new Select("Pick Motion File", domElement, []);
        this.animationPicker.setToolTip(this.interfaces.tooltips.pickMotionFile, "bottomToolTip");
        this.animationPicker.div.classList.add("animationFilePicker");
        this.animationPicker.select.onchange = () => {
            this.currAnimationTable = this.allAnimations[this.animationPicker.getSelectedIndex()];
            this.resetSettings();
        }

        // prerender line button
        this.prerenderLineButton = new Button("Prerender Line", domElement, () => {
            this.prerenderLine(null, [], true);
        });
        this.prerenderLineButton.setToolTip(this.interfaces.tooltips.prerenderLine, "bottomToolTip");

        // animation slider
        this.animationSlider = new Slider("Animation Time", domElement, 0, 1, 0.0001, 0);
        this.animationSlider.removeElement(0);
        this.animationSlider.removeElement(0);
        this.animationSlider.slider.oninput = () => {
            if (this.currAnimationTable.length == 0) {
                return;
            }
            let startTime = Number(this.startTimeTextInput.textInput.value);
            let endTime = Number(this.endTimeTextInput.textInput.value);
            let time = lerp(startTime, endTime, this.animationSlider.slider.value);
            this.sliderUpdate(time)
        }
        this.animationSlider.hideLabel();
        this.animationSlider.removeDiv()

        // start time input
        this.startTimeTextInput = new TextInput("Start Time", domElement);
        this.startTimeTextInput.setToolTip(this.interfaces.tooltips.startTime, "bottomToolTip");
        this.startTimeTextInput.textInput.onchange = () => {
            let time = Number(this.startTimeTextInput.textInput.value);
            if (isNaN(time) || time < 0) {
                this.startTimeTextInput.textInput.value = 0;
                this.timeBar.updateRange(0, -1, -1);
            } else {
                this.timeBar.updateRange(time, -1, -1);
            }
            this.parallelMotions.forEach(group => {
                if(group.syncCheck.checkbox.checked) {
                    group.animationInterface.setTimeRange(Number(this.startTimeTextInput.textInput.value), Number(this.endTimeTextInput.textInput.value))
                }
            })
        }

        // end time input
        this.endTimeTextInput = new TextInput("End Time", domElement);
        this.endTimeTextInput.setToolTip(this.interfaces.tooltips.endTime, "bottomToolTip");
        this.endTimeTextInput.textInput.onchange = () => {
            let time = Number(this.endTimeTextInput.textInput.value);
            if (this.currAnimationTable.length > 0) {
                if (isNaN(time) || time > Number(this.currAnimationTable[this.currAnimationTable.length - 1][0])) {
                    this.endTimeTextInput.textInput.value = Number(this.currAnimationTable[this.currAnimationTable.length - 1][0]);
                    this.timeBar.updateRange(-1, Number(this.currAnimationTable[this.currAnimationTable.length - 1][0]), -1);
                } else {
                    this.timeBar.updateRange(-1, time, -1);
                }
            } else {
                this.endTimeTextInput.textInput.value = 0;
            }
            this.parallelMotions.forEach(group => {
                if(group.syncCheck.checkbox.checked) {
                    group.animationInterface.setTimeRange(Number(this.startTimeTextInput.textInput.value), Number(this.endTimeTextInput.textInput.value))
                }
            })
        }

        // time bar
        this.timeBar = new TimeBar("timebar", domElement, this, this.interfaces);

        // -------------- parallel motions ----------------

        // robotSelector
        this.parallelRobotSelector = new Select("Select Parallel Robot", domElement),
        this.interfaces.robot.getAllRobots().forEach(robot => {
            if(robot != this.robot) {
                this.parallelRobotSelector.addOption([robot.robotName, robot]);
            }
        })

        // add button
        this.addParallelMotionButton = new Button("Add Parallel Motion", domElement, () => {
            this.addParallelMotion(this.parallelRobotSelector.getSelectedName())
        })

        this.isParallelMotionShown = true
        this.toggleParallelMotionsButton = new Button("Hide", domElement, () => {
            this.toggleShowParallelMotions(!this.isParallelMotionShown)
        })

        // create rows and add elements
        this.row1Container = new Container("row1Container", domElement);
        this.row2Container = new Container("row2Container", domElement);
        this.row3Container = new Container("row3Container", "multiMotionDiv");
        this.parallelMotionContainer = new Container("parallelMotionContainer", "multiMotionDiv");
        this.resetContainers();
    }

    /*
        On switching robots, the UI is reset, adding all the elements back to their respective containers in the right order
    */
    resetContainers() {
        this.row1Container.addBlock(this.animationSliderTextInput, this.playContainer, this.markContainer, this.speedTextInput, this.playModeContainer, this.stepAmountTextInput,
            this.rowStepCheck, this.stepContainer, this.loader, this.urlInput, this.loadURL, this.animationPicker, this.prerenderLineButton);
        this.row2Container.addBlock(this.startTimeTextInput, this.timeBar, this.endTimeTextInput);
        this.row3Container.addBlock(this.parallelRobotSelector, this.addParallelMotionButton, this.toggleParallelMotionsButton);

        // containers for parallel motions
        this.parallelMotions.forEach(group => {
            group.animationPicker.hideLabel()
            group.motionGroupRow1Container.addBlock(group.robotLabel, group.motionPickerLabel, group.animationPicker, group.syncCheck, group.offset, group.removeParallelMotion)
            group.motionGroupRow2Container.addBlock(group.sliderRow.startTime, group.sliderRow.timeBar, group.sliderRow.endTime)
            group.sliderRow.timeBar.toggleClickable(!group.syncCheck.checkbox.checked);
            if(group.syncCheck.checkbox.checked) {
                group.animationInterface.loopCheck.checkbox.checked = false;
                group.animationInterface.bounceCheck.checkbox.checked = false;
                group.animationInterface.setTimeRange(Number(this.startTimeTextInput.textInput.value), Number(this.endTimeTextInput.textInput.value))
            }
        })
    }

    /*
        Function for other interfaces to call to update the slider and time if a motion exists
    */
    sliderUpdate(time) {
        if (this.currAnimationTable.length == 0) {
            return;
        }
        this.updateTime(time);
    }

    /**
     * Reset the settings of the current robot's UI for motions
     * Also resets time on motion playbar
     * Used for when switching motions or adding new ones (which switches it)
     */
    resetSettings() {
        this.animationSliderTextInput.textInput.value = 0;
        this.exactTime = 0;
        this.animationSlider.slider.value = 0;
        this.interfaces.lines.endCurrentLine();
        this.playCheck.checkbox.checked = false;
        this.startTimeTextInput.textInput.value = 0;
        if (this.currAnimationTable.length > 0) {
            this.endTimeTextInput.textInput.value = this.currAnimationTable[this.currAnimationTable.length - 1][0];
        }
        let endTime = Number(this.currAnimationTable[this.currAnimationTable.length - 1][0])
        this.timeBar.updateRange(0, endTime, endTime);
        this.timeBar.setMotion(this.animationPicker.getSelectedName());
        this.updateTime(0);
    }

    /*
        Given a time input, updates all elements involving time of motion
        This includes the time bar, other parallel motions, the slider time text, and the row/step of the motion
    */
    updateTime(time, overrideRestrictions = false) {
        if (this.currAnimationTable.length == 0 || typeof time != 'number' || isNaN(time)) {
            return;
        }
        // get start and end time
        let startTime = Number(this.startTimeTextInput.textInput.value);
        let endTime = Number(this.endTimeTextInput.textInput.value);
        if (overrideRestrictions) {
            startTime = 0;
            endTime = Number(this.currAnimationTable[this.currAnimationTable.length - 1][0]);
        }
        if (isNaN(startTime) || isNaN(endTime) || startTime < 0 || endTime > Number(this.currAnimationTable[this.currAnimationTable.length - 1][0])) {
            return;
        }
        if (this.loopCheck.checkbox.checked) {
            if (time > endTime) {
                time = startTime;
            } else if (time < startTime) {
                time = endTime
            }
        } else {
            let flip = false;
            if (time > endTime) {
                time = endTime;
                flip = true;
            } else if (time < startTime) {
                time = startTime;
                flip = true;
            }
            if (this.bounceCheck.checkbox.checked && flip) {
                this.speedTextInput.textInput.value = -Number(this.speedTextInput.textInput.value)
            }
        }

        // approximation
        let precision = 6;
        let timeCopy = time;
        while (timeCopy > 1) {
            precision++;
            timeCopy = timeCopy / 10;
        }
        let estimateTime = Number(time.toPrecision(precision));

        // updates
        this.animationSlider.slider.value = (estimateTime - startTime) / (endTime - startTime);
        this.animationSlider.label.innerHTML = "Animation Time: " + String(estimateTime);
        this.animationSliderTextInput.textInput.value = estimateTime;
        animateRobot(time, this.robot, this.currAnimationTable);
        this.interfaces.joints.updateSliders(this.robot);
        this.interfaces.lines.updateLine(time);
        this.stepText.label.innerHTML = "Step: " + getTableIndex(time, this.currAnimationTable) + " / " + (this.currAnimationTable.length - 2);
        this.timeBar.updateTime(time);
        this.timeBar.updateCanvas();

        if (this.autoCameraCheck.checkbox.checked) {
            updateCamera(this.exactTime, Number(this.currAnimationTable[this.currAnimationTable.length - 1][0]), this.timeBar.getBookmarkList(), this.interfaces.controls);
        }

        this.parallelMotions.forEach(group => {
            if(group.syncCheck.checkbox.checked) {
                group.animationInterface.updateTime(time + Number(group.offset.textInput.value), true);
            }
        })
        this.exactTime = time;
    }

    /**
     * Step forward in time of the currently selected
     * @param {Number} amount Amount of time to step forward (in seconds)
     */
    step(amount) {
        let time = this.exactTime;
        if (this.rowStepCheck.checkbox.checked) {
            let currIndex = getTableIndex(time, this.currAnimationTable);
            let nextIndex = amount >= 0 ? Math.ceil(currIndex) : Math.floor(currIndex);
            if (currIndex === nextIndex) {
                nextIndex += amount >= 0 ? 1 : -1;
            }
            if (nextIndex < 0 || nextIndex >= this.currAnimationTable.length - 1) {
                return;
            }
            time = Number(this.currAnimationTable[nextIndex + 1][0]);
        }
        else {
            time += amount;
        }
        this.updateTime(time);
    }

    /**
     * Plays the motion, either automatically, or by call
     * @param {Boolean} forcePlay If the play checkbox is not selected, forcePlay will make it play
     */
    animateSlider(forcePlay = false) {
        let newTime = new Date();
        let inc = ((newTime - this.lastTime) / 1000) * Number(this.speedTextInput.textInput.value);
        this.lastTime = newTime;

        // update all parallel sliders
        this.parallelMotions.forEach(group => {
            group.robotGroup.animationInterface.lastTime = newTime + Number(group.offset.textInput.value);
        })

        if ((this.playCheck.checkbox.checked || forcePlay) && inc != 0 && this.currAnimationTable.length > 0 && !isNaN(inc)) {
            let nextTime = this.exactTime + inc;
            this.updateTime(nextTime);

            this.parallelMotions.forEach(group => {
                let groupNewTime = group.robotGroup.animationInterface.exactTime + inc;
                group.robotGroup.animationInterface.updateTime(groupNewTime);
            })
        }
    }

    /**
     * Add motion data to the interface
     * @param {Number[][]} data the motion data
     * @param {String} name name of the file
     */
    createAnimationTable(data, name = "") {
        let table = [];
        data.forEach(config => {
            table.push(config);
        })
        let startTime = table[1][0]
        let lastRow = table[table.length - 1];
        if (lastRow.length == 1) {
            table.pop();
        }
        lastRow = table[table.length - 1]

        // checks if the duration is over 1000, meaning time is in milliseconds
        let isMilli = (table[table.length - 1][0] - startTime) > 1000
        let isMicro = (table[table.length - 1][0] - startTime) > 1000000
        let isNano = (table[table.length - 1][0] - startTime) > 1000000000

        // zeroing time and convert
        for(let i = 1; i < table.length; i++) {
            table[i][0] -= startTime
            if(isMilli) {
                table[i][0] /= 1000
            }
            if(isMicro) {
                table[i][0] /= 1000
            }
            if(isNano) {
                table[i][0] /= 1000
            }
        }

        this.allAnimations.push(table);
        let optionName;
        if (name == "") {
            optionName = this.loader.getFiles()[0].name;
        } else {
            optionName = name;
        }
        this.animationPicker.addOption([optionName, table]);
        this.currAnimationTable = table;
        this.animationPicker.setSelected(optionName);
        this.resetSettings();
    }

    /**
     * 
     * @param {String} url link to the animation
     * @param {Function)} afterFunction callback function for after loading in the animation
     */
    loadAnimation(url, afterFunction = () => { }) {
        this.animationURLs.push(url);
        fetchFromURL(url, (blob) => {
            let fileName = url.substring(url.lastIndexOf("/") + 1, url.lastIndexOf("."));
            parseCSV(blob, (data) => {
                this.createAnimationTable(data, fileName);
                afterFunction();
            });
        });
    }

    prerenderLine(motionName = null, cuts = [], entireMotion = false) {
        let saveTime = this.getCurrentTime();
        if(motionName != null) {
            if(!this.setMotion(motionName)) {
                console.log("Invalid Table Name:", motionName);
                return;
            }
        }
        let table = this.currAnimationTable;
        if (table.length == 0) {
            console.log("Empty Animation Table or No Animations Loaded");
            return;
        }

        const MAX_STEPS = 3000;
        let step = Math.ceil(table.length / MAX_STEPS);
        if (step == 0) {
            step++;
        }

        animateRobot(0, this.robot, table);

        let drawLine = (l) => {
            let t = Number(table[l][0]);
            this.exactTime = t;
            animateRobot(t, this.robot, table);
            this.robotGroup.updateEndEffector()
            if (l + step >= table.length - 1) {
                this.interfaces.lines.updateLine(t, true);
            } else {
                this.interfaces.lines.updateLine(t, false);
            }
        }

        if(entireMotion) {
            this.interfaces.lines.createNewLine(this.robotGroup);
            for (let i = 1; i < table.length; i += step) {
                drawLine(i)
            }
            let motionPlaneData = this.interfaces.lines.getCurrentLine().getLinePlane()
            let motionData = this.interfaces.lines.getCurrentLine().getPointsData()
            this.interfaces.lines.endCurrentLine();

            let times = this.robotGroup.dataInterface.getPoseHeuristics(motionData)
            this.robotGroup.dataInterface.getOverallCamera(times, motionPlaneData, true)
            this.interfaces.cameraInterface.addCameraView()
        } else {
            animateRobot(0, this.robot, table);
            let i = 1;
            let times = []
            let currCut = cuts[0]
            let currCutIndex = 0
            this.interfaces.lines.createNewLine(this.robotGroup);
            while(i < table.length) {
                drawLine(i)
                // cut
                if(Number(table[i][0]) >= currCut) {
                    this.interfaces.lines.updateLine(table[i][0], true);
                    // add ghosts and times
                    let timeStamps = this.timeBar.getBookmarkTimes()
                    let start = currCutIndex == 0 ? 0 : cuts[currCutIndex-1]
                    let end = currCut
                    
                    let k = 0
                    let lastIndex = -1
                    timeStamps.forEach(timeStamp => {
                        if(timeStamp.time >= start && timeStamp.time < end) { 
                            let ghosts = this.createGhosts(timeStamp.time)
                            ghosts.forEach(ghost => {
                                this.interfaces.lines.addGhost(ghost, timeStamp.time)
                            })
                            times.push(timeStamp.time)
                            if(k > lastIndex) {
                                lastIndex = k
                            }
                        }
                        k++
                    })
                    if(lastIndex > -1) {
                        times.push(timeStamps[lastIndex].time)
                    }

                    let motionPlaneData = this.interfaces.lines.getCurrentLine().getLinePlane()
                    this.interfaces.lines.endCurrentLine(true);

                    times = []
                    this.interfaces.lines.createNewLine(this.robotGroup);
                    currCutIndex++
                    if(currCutIndex >= cuts.length) {
                        currCut = 1000000
                    } else {
                        currCut = cuts[currCutIndex]
                    }
                }
                i+=step
            }

            // last line
            this.interfaces.lines.updateLine(table[table.length-1][0], true);
            let timeStamps = this.timeBar.getBookmarkTimes()
            let start = currCutIndex == 0 ? 0 : cuts[currCutIndex-1]
            let end = currCut
            timeStamps.forEach(timeStamp => {
                if(timeStamp.time >= start && timeStamp.time < end) {
                    let ghosts = this.createGhosts(timeStamp.time)
                    ghosts.forEach(ghost => {
                        this.interfaces.lines.addGhost(ghost, timeStamp.time)
                    })
                    times.push(timeStamp.time)
                }
            })
            times.push(table[table.length-1][0])
            let motionPlaneData = this.interfaces.lines.getCurrentLine().getLinePlane()
            this.interfaces.lines.endCurrentLine(true);
        }

        animateRobot(saveTime, this.robot, table);
        // this.showGhosts()
        this.exactTime = saveTime;
    }

    setFocusJoint(newJoint) {
        this.focusJoint = newJoint;
    }

    getAnimationTable() {
        return this.currAnimationTable;
    }

    getCurrentTime() {
        return this.exactTime;
    }

    getSelectedAnimationName() {
        return this.animationPicker.getSelectedName();
    }

    hideUI() {
        this.row1Container.removeDiv();
        this.row2Container.removeDiv();
        this.row3Container.removeDiv();
        this.parallelMotionContainer.removeDiv();
    }

    showUI() {
        this.row1Container.addDiv();
        this.row2Container.addDiv();
        this.row3Container.addDiv();
        this.parallelMotionContainer.addDiv();
        this.lastTime = new Date();
        this.animationPicker.showLabel();
        this.resetContainers();
        this.timeBar.toggleClickable(true)
    }

    getAnimationURLs() {
        return this.animationURLs;
    }

    updateTimeBar() {
        this.timeBar.updateCanvas();
    }

    setMotion(motionName) {
        let index = this.animationPicker.getIndexOf(motionName);
        if(index != -1) {
            this.animationPicker.setSelected(motionName);
            this.currAnimationTable = this.allAnimations[this.animationPicker.getSelectedIndex()];
            this.resetSettings();
            return true;
        } else {
            return false;
        }
    }

    createGhosts(time) {
        let ghosts = []
        let ghost = this.robot.clone();
        ghost.traverse(c => {
            if (c.material) {
                cloneMaterial(c);
                recurseMaterialTraverse(c.material, (material) => {
                    material.opacity = 0.5;
                })
            }
        })
        ghosts.push(ghost)
        this.interfaces.scene.add(ghost);
        ghost.position.copy(this.robot.getWorldPosition(new T.Vector3()));
        ghost.rotation.setFromQuaternion(this.robot.getWorldQuaternion(new T.Quaternion()));
        animateRobot(time, ghost, this.currAnimationTable);

        // parallel motions
        this.parallelMotions.forEach(group => {
            group.animationInterface.updateTime(time + Number(group.offset.textInput.value), true);
            let subGhost = group.robotGroup.robot.clone()
            let rotationParent = new T.Group();
            rotationParent.add(subGhost)
            subGhost.rotation.x = -Math.PI / 2;
            subGhost.traverse(c => {
                if (c.material) {
                    cloneMaterial(c);
                    recurseMaterialTraverse(c.material, (material) => {
                        material.opacity = 0.5;
                    })
                }
            })
            subGhost.position.copy(group.robotGroup.robot.getWorldPosition(new T.Vector3()));
            subGhost.rotation.setFromQuaternion(group.robotGroup.robot.getWorldQuaternion(new T.Quaternion()));
            this.interfaces.scene.add(subGhost);
            ghosts.push(subGhost)
        })

        return ghosts
    }

    showGhost(time) {
        // console.log("showing ghost", time)
        this.ghostRobot.visible = true;
        this.ghostRobot.position.copy(this.robot.getWorldPosition(new T.Vector3()));
        this.ghostRobot.rotation.setFromQuaternion(this.robot.getWorldQuaternion(new T.Quaternion()));
        animateRobot(time, this.ghostRobot, this.currAnimationTable);
    }

    hideGhost() {
        this.ghostRobot.visible = false;
    }

    getBookmarksData() {
        return this.timeBar.getAllBookmarkLists();
    }

    addParallelOption(robot) {
        this.parallelRobotSelector.addOption([robot.robotName, robot]);
    }

    setTimeRange(start, end) {
        let startTime = Math.max(start, 0);
        this.startTimeTextInput.textInput.value = startTime
        let endTime = Math.min(Number(this.currAnimationTable[this.currAnimationTable.length - 1][0]), end);
        this.endTimeTextInput.textInput.value = endTime
        this.timeBar.updateRange(startTime, endTime, -1);
    }

    addParallelMotion(robotName, synced=false, initialOffset=0) {

        let parallelMotionGroupContainer = new Container("parallelMotionGroupContainer", "parallelMotionContainer", true);
        let motionGroupRow1Container = new Container("motionGroupRow1Container", "parallelMotionGroupContainer")
        let motionGroupRow2Container = new Container("motionGroupRow2Container", "parallelMotionGroupContainer")
        this.parallelMotionContainer.addBlock(parallelMotionGroupContainer)

        let robotGroup = this.interfaces.robot.getRobotGroup(robotName);
        let parallelAnimationPicker = robotGroup.animationInterface.animationPicker;
        parallelAnimationPicker.hideLabel()
        let sliderRow = {
            startTime: robotGroup.animationInterface.startTimeTextInput,
            timeBar: robotGroup.animationInterface.timeBar, 
            endTime: robotGroup.animationInterface.endTimeTextInput
        }

        let motionPickerLabel = new TextBox("parallelRobotMotionLabel", "motionGroupRow1Container", "Motion: ")
        let label = new TextBox("parallelRobotLabel", "motionGroupRow1Container", robotName)
        let syncCheck = new Checkbox("Synchronize with main motion", "motionGroupRow1Container", synced)
        let offset = new TextInput("Offset", "motionGroupRow1Container", initialOffset.toString())

        syncCheck.checkbox.onchange = () => {
            sliderRow.timeBar.toggleClickable(!syncCheck.checkbox.checked);
            if(syncCheck.checkbox.checked) {
                robotGroup.animationInterface.loopCheck.checkbox.checked = false;
                robotGroup.animationInterface.bounceCheck.checkbox.checked = false;
                robotGroup.animationInterface.setTimeRange(Number(this.startTimeTextInput.textInput.value), Number(this.endTimeTextInput.textInput.value))
                robotGroup.animationInterface.updateTime(this.exactTime);
            }
        }

        let group = {
            robotName: robotName,
            robotGroup: robotGroup,
            animationInterface: robotGroup.animationInterface,
            animationPicker: parallelAnimationPicker,
            robotLabel: label,
            motionPickerLabel: motionPickerLabel,
            syncCheck: syncCheck,
            sliderRow: sliderRow,
            motionGroupRow1Container: motionGroupRow1Container,
            motionGroupRow2Container: motionGroupRow2Container,
            offset: offset,
            parallelMotionGroupContainer: parallelMotionGroupContainer
        }

        let removeParallelMotion = new Button("Remove Parallel Motion", "motionGroupRow1Container", () => {
            this.parallelMotions.splice(this.parallelMotions.indexOf(group), 1)
            sliderRow.timeBar.toggleClickable(true)
            parallelMotionGroupContainer.removeDiv();
        })

        if(!this.isParallelMotionShown) {
            parallelMotionGroupContainer.removeDiv();
        }

        group.removeParallelMotion = removeParallelMotion

        motionGroupRow1Container.addBlock(label, motionPickerLabel, parallelAnimationPicker, syncCheck, offset, removeParallelMotion)
        motionGroupRow2Container.addBlock(sliderRow.startTime, sliderRow.timeBar, sliderRow.endTime)

        this.parallelMotions.push(group)
    }

    toggleShowParallelMotions(show) {
        if(!show) {
            this.isParallelMotionShown = false
            this.toggleParallelMotionsButton.button.innerHTML = "Show"
            this.parallelMotions.forEach(group => {
                group.parallelMotionGroupContainer.removeDiv()
            })
        } else {
            this.isParallelMotionShown = true
            this.toggleParallelMotionsButton.button.innerHTML = "Hide"
            this.parallelMotions.forEach(group => {
                group.parallelMotionGroupContainer.addDiv()
            })
        }
    }

    getParallelMotionNames() {
        return this.parallelMotions.map(motion => [motion.robotName, motion.syncCheck.checkbox.checked, motion.offset.textInput.value])
    }

    addBookmark(time) {
        this.timeBar.addBookmark(this.animationPicker.getSelectedName(), time);
    }

    simplifyBar() {
        this.markContainer.removeDiv();
        this.speedTextInput.removeDiv();
        this.stepAmountTextInput.removeDiv();
        this.rowStepCheck.removeDiv();
        this.stepContainer.removeDiv();
        this.loader.removeDiv();
        this.urlInput.removeDiv();
        this.loadURL.removeDiv();
        this.prerenderLineButton.removeDiv();
        this.autoCameraCheck.removeDiv();

        this.startTimeTextInput.removeDiv();
        this.endTimeTextInput.removeDiv();

        this.row3Container.removeDiv();
        this.parallelMotionContainer.removeDiv();

        document.getElementById("bottomDiv").classList.add("bottomDiv-readOnly")
        this.row1Container.div.classList.add("row1Container-readOnly")
        this.animationSliderTextInput.div.classList.add("sliderText-readOnly")
        this.playContainer.div.classList.add("playContainer-readOnly", "horizontalContainer")
        this.playContainer.div.classList.remove("verticalContainer")
        this.playCheck.div.classList.add("playCheck-readOnly")
        this.animationSlider.div.classList.add("animationSlider-readOnly")
    }
}